import React from 'react';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  /** Select options */
  options: SelectOption[];
  /** Current value */
  value?: string;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Error state */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Select Atom Component
 * 
 * A styled select dropdown with consistent error handling.
 * 
 * @example
 * ```tsx
 * <Select
 *   options={[
 *     { value: 'pending', label: 'Pending' },
 *     { value: 'completed', label: 'Completed' }
 *   ]}
 *   value={status}
 *   onChange={setStatus}
 *   placeholder="Select status"
 * />
 * ```
 */
export const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  error = false,
  errorMessage,
  placeholder,
  className = '',
  ...props
}) => {
  const baseClasses = 'w-full px-3 py-2 border rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 appearance-none bg-white';
  
  const stateClasses = error 
    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
    : 'border-secondary-300 focus:border-primary-500 focus:ring-primary-200';

  const classes = [
    baseClasses,
    stateClasses,
    className,
  ].filter(Boolean).join(' ');

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange?.(e.target.value);
  };

  return (
    <div className="relative">
      <select
        value={value || ''}
        onChange={handleChange}
        className={classes}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
      
      {/* Custom dropdown arrow */}
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <ChevronDown className={`w-4 h-4 ${error ? 'text-red-400' : 'text-secondary-400'}`} />
      </div>
      
      {error && errorMessage && (
        <p className="mt-1 text-sm text-red-600">{errorMessage}</p>
      )}
    </div>
  );
};
